// Phase 2D-2 — pure, dependency-free truth about a SuperAdmin invitation's delivery outcome and
// lifecycle bucket. Kept OUT of the component so it is unit-testable and cannot drift from the backend
// tokens in api/superadmin_invitations.py (resolve_delivery_outcome + the DS_* delivery-status tokens).
//
// The whole point of Phase 2D: the owner UI must never imply an invitee "received" a link that was
// redirected to the staging safety mailbox or suppressed by policy. `reached` encodes that truth.

// Open (not-yet-finished) invitation states — an invite in any of these is still actionable
// (Resend / Cancel) and the account is pending. Mirrors RESENDABLE_STATES ∪ {mfa_pending}.
export const OPEN_INVITE_STATES = [
  'pending_delivery',
  'delivery_failed',
  'pending_activation',
  'mfa_pending',
  'delivery_redirected',
  'delivery_suppressed',
];

export function isOpenInviteState(state) {
  return OPEN_INVITE_STATES.includes(state);
}

// Truthful pill copy for the governed staging non-delivery lifecycle states. (The remaining states are
// labelled in the component alongside their existing Tailwind classes.)
export const INVITE_STATE_LABEL = {
  delivery_redirected: 'Redirected (staging)',
  delivery_suppressed: 'Blocked (staging policy)',
};

// delivery_status token → truthful owner-facing outcome.
//   reached: TRUE only when the intended invitee actually received the link (provider-accepted intended
//            send). Redirected / suppressed / failed / pending are all FALSE.
//   tone:    success | warning | error | neutral  (maps to toast variant + hint colour in the component)
const DELIVERY = {
  pending: {
    reached: false, tone: 'neutral', label: 'Sending…', detail: '',
  },
  sent: {
    reached: true, tone: 'success', label: 'Delivered to recipient', detail: '',
  },
  redirected_for_staging: {
    reached: false, tone: 'warning', label: 'Redirected (staging safety)',
    detail: 'Sent to the staging safety mailbox — the invitee did NOT receive it. Add the address to the '
      + 'staging allowlist, then resend.',
  },
  suppressed: {
    reached: false, tone: 'warning', label: 'Blocked by staging policy',
    detail: 'Not transmitted — the address is not on the staging allowlist. Nothing reached the invitee.',
  },
  failed: {
    reached: false, tone: 'error', label: 'Delivery failed',
    detail: 'The email provider rejected the message.',
  },
};

export function deliveryOutcome(deliveryStatus) {
  return DELIVERY[deliveryStatus]
    || { reached: false, tone: 'neutral', label: deliveryStatus || 'Unknown', detail: '' };
}

// Compose a truthful toast payload for a send/resend/reinvite result. `action` varies only the success
// wording ('sent' | 'resent' | 'setup'); every non-delivered outcome states plainly what happened and
// never claims the invitee got the link.
export function deliveryToast(deliveryStatus, action = 'sent') {
  const o = deliveryOutcome(deliveryStatus);
  if (o.reached) {
    const title = action === 'resent' ? 'Invitation resent'
      : action === 'setup' ? 'Setup link sent'
        : 'Invitation sent';
    return {
      title,
      description: 'The activation link was emailed to the intended recipient. They must set a password '
        + 'and enrol an authenticator before they can sign in.',
      variant: undefined,
    };
  }
  return {
    title: `${o.label} — invitee not reached`,
    description: o.detail || `The email was not delivered (${deliveryStatus || 'unknown'}).`,
    variant: 'destructive',
  };
}
